/**
 * AddressBookDialog — manage your personal contacts (the Vault CRM, shared with
 * the contact-book arche). List with search, add, and delete. Contacts are
 * holder-scoped, so they're the same across email and Contacts, and span ALL
 * connected accounts (no account picker). A "From your mail" section surfaces
 * addresses auto-harvested from your Sent mail across every account that aren't
 * in the CRM yet, each promotable in one tap.
 */

import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Trash2, Plus } from 'lucide-react';
import {
  useAddressBook,
  useHarvestedEmailContacts,
  useCreateContact,
  useDeleteContact,
  contactDisplayName,
  contactPrimaryEmail,
} from '../hooks/useAddressBook';

interface AddressBookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddressBookDialog({ open, onOpenChange }: AddressBookDialogProps) {
  const [search, setSearch] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const { data: contacts, isLoading } = useAddressBook(open, search);
  // Harvested suggestions across ALL accounts (no account scoping).
  const { data: harvested } = useHarvestedEmailContacts(undefined, open, search);
  const create = useCreateContact();
  const del = useDeleteContact();

  // Harvested addresses not already curated in the CRM — the promotable set.
  const crmEmails = useMemo(
    () =>
      new Set(
        (contacts ?? []).flatMap((c) =>
          c.emails.map((e) => e.value?.trim().toLowerCase()).filter(Boolean),
        ),
      ),
    [contacts],
  );
  const suggestions = useMemo(
    () => (harvested ?? []).filter((h) => !crmEmails.has(h.emailAddress.trim().toLowerCase())),
    [harvested, crmEmails],
  );

  const add = () => {
    if (!email.trim()) return;
    create.mutate(
      { emailAddress: email.trim(), displayName: name.trim() || null },
      {
        onSuccess: () => {
          setEmail('');
          setName('');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Contacts</DialogTitle>
        </DialogHeader>
        {/* Fixed layout: search + add form are pinned, only the list scrolls —
            so the dialog keeps a constant size as you filter (no jumping). */}
        <div className="space-y-3">
          <Input
            placeholder="Search contacts"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {/* One list: your saved contacts first, then everyone you've emailed
              (harvested from Sent mail) that isn't saved yet — so the address
              book shows all your previous recipients, not just curated ones.
              Harvested rows carry a subtle "from mail" tag and a one-tap save. */}
          <div className="h-72 overflow-y-auto rounded border">
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (contacts?.length ?? 0) === 0 && suggestions.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No contacts.</p>
            ) : (
              <ul className="divide-y">
                {(contacts ?? []).map((c) => {
                  const label = contactDisplayName(c);
                  const primary = contactPrimaryEmail(c);
                  return (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{label}</span>
                        {label !== primary && primary && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {primary}
                          </span>
                        )}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Delete"
                        onClick={() => del.mutate(c.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  );
                })}
                {suggestions.map((h) => (
                  <li
                    key={h.contactId}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate font-medium">
                          {h.displayName || h.emailAddress}
                        </span>
                        <span className="shrink-0 rounded bg-muted px-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                          from mail
                        </span>
                      </span>
                      {h.displayName && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {h.emailAddress}
                        </span>
                      )}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Save to contacts"
                      aria-label={`Add ${h.emailAddress} to contacts`}
                      disabled={create.isPending}
                      onClick={() =>
                        create.mutate({
                          emailAddress: h.emailAddress,
                          displayName: h.displayName,
                        })
                      }
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="space-y-1 border-t pt-3">
            <Input
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button
              size="sm"
              className="gap-1"
              disabled={!email.trim() || create.isPending}
              onClick={add}
            >
              <Plus className="h-3.5 w-3.5" /> Add contact
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
